import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { IAuthToken, useAuthContext, useIdentity } from "context/auth";
import { ZendeskAPI } from "react-zendesk";
import {
    useAuthenticatedRequestCreator,
    usePublicRequestCreator,
} from "./useRequests";

const useGetImpersonationToken = () => {
    const getImpersonationToken = useAuthenticatedRequestCreator<
        IAuthToken,
        string
    >((email) => ({
        method: "post",
        url: "/account/impersonate",
        headers: { "Content-Type": "application/json" },
        data: {
            email,
        },
    }));

    return getImpersonationToken;
};

export interface LoginCreds {
    username: string;
    password: string;
}

export const useLogin = () => {
    const { setToken, refreshToken } = useAuthContext();
    const queryClient = useQueryClient();

    const {
        isLoggedIn,
        roles,
        twoFactorTokenRequired,
        twoFactorSetupRequired,
        isAdmin,
        isCustomer
    } = useIdentity();

    const authTokenRequest = usePublicRequestCreator<IAuthToken, LoginCreds>(
        ({ username, password }) => ({
            method: "post",
            url: "/account/login",
            headers: { "Content-Type": "application/json" },
            data: {
                username,
                password,
            },
        }),
        // Tell the request we're going to handle 401s ourselves
        [401]
    );

    const refreshTokenRequest = usePublicRequestCreator<IAuthToken, string>(
        (rt) => ({
            method: "post",
            url: "/account/login/refresh",
            headers: { "Content-Type": "application/json" },
            data: { refreshToken: rt }
        })
    );

    const { mutateAsync: authenticate, ...rest } = useMutation(
        async (creds: LoginCreds) => {
            try {
                const authToken = await authTokenRequest(creds);
                setToken(authToken);
                await queryClient.invalidateQueries();
                // Reauthenticate with Zendesk cheat widget
                // TODO: This doesn't seem to work, but the authentication doesn't do anything right now anyway
                ZendeskAPI("webWidget", "chat:reauthenticate");
            } catch (error) {
                if (axios.isAxiosError(error)) {
                    const { error: e } = error?.response?.data as any;
                    throw new Error(e ?? "Login Failed");
                }
                throw error;
            }
        }
    );

    const { mutateAsync: refreshAuthToken } = useMutation(async () => {
        if (!refreshToken) {
            return;
        }

        const authToken = await refreshTokenRequest(refreshToken);
        setToken(authToken);
        await queryClient.invalidateQueries();
    });

    return {
        authenticate,
        isLoggedIn,
        roles,
        twoFactorTokenRequired,
        twoFactorSetupRequired,
        isAdmin,
        isCustomer,
        refreshAuthToken,
        ...rest
    };
};

export const useTwoFactorToken = () => {
    const { setToken } = useAuthContext();

    const verify = useAuthenticatedRequestCreator<IAuthToken, string>(
        (token) => ({
            method: "post",
            url: "/account/verify-token",
            data: { token }
        }),
        [401]
    );

    const { mutateAsync: verifyToken, isLoading, isError } = useMutation(
        async (token: string) => {
            const authToken = await verify(token);

            setToken(authToken);
        }
    );

    return { verifyToken, isLoading, isError };
}

export const useCustomerImpersonation = () => {
    const { startImpersonating } = useAuthContext();
    const getImpersonationToken = useGetImpersonationToken();

    const impersonate = async (emailAddress: string) => {
        const token = await getImpersonationToken(emailAddress);
        startImpersonating(token);
    };

    return { impersonate };
};
